export interface GalleryImage {
  url: string;
  label: string;
  caption: string;
  aspect: 'landscape' | 'portrait' | 'square';
}

export interface CaseItem {
  id: string;
  category: string;
  categoryEn: string;
  tag: string;
  tagEn: string;
  tagColor: string;
  title: string;
  subtitle: string;
  client: string;
  clientType: string;
  before: string;
  after: string;
  period: string;
  scope: string;
  deliverables: string[];
  img: string;
  detail: {
    challenge: string;
    approach: string[];
    process: { phase: string; title: string; body: string }[];
    results: { label: string; value: string }[];
    quote: { text: string; author: string };
    images: string[];
    gallery: GalleryImage[];
  };
}

export const casesList: CaseItem[] = [
  {
    id: '01',
    category: 'ブランディング',
    categoryEn: 'Branding',
    tag: '医療',
    tagEn: 'Healthcare',
    tagColor: 'text-rose-700 border-rose-300',
    title: '地域医療法人のブランド再構築と患者コミュニケーション設計',
    subtitle: '「地域に根ざした病院」を、誰もが語れる言葉に。',
    client: '地域医療法人',
    clientType: '病床数120床・職員数230名',
    before: '「地域に根ざした病院」という言葉だけが残り、何が強みかを誰も言えなかった。',
    after: '患者・スタッフ・地域が共に語れる「物語の軸」が生まれ、紹介患者数が前年比34%増加。',
    period: '2023年4月〜2024年3月（12ヶ月）',
    scope: '意味設計・ブランド戦略 / コンテンツ設計 / スタッフ研修',
    deliverables: ['ブランドコンセプト策定', 'ミッション・ビジョン・バリュー設計', '患者向けパンフレット', 'Webサイトリニューアル', '院内サイン計画', 'スタッフ研修プログラム'],
    img: 'https://readdy.ai/api/search-image?query=modern%20Japanese%20medical%20clinic%20interior%2C%20clean%20white%20walls%2C%20natural%20light%2C%20minimalist%20design%2C%20warm%20atmosphere%2C%20no%20people%2C%20architectural%20photography%2C%20soft%20beige%20tones&width=800&height=540&seq=case001&orientation=landscape',
    detail: {
      challenge: '創業30年を超える地域の中核病院。「地域に根ざした医療」を掲げてきたが、その言葉が形骸化し、スタッフ自身も「うちの病院の強みは何か」を語れない状態になっていた。患者数は横ばいが続き、近隣に新しいクリニックが増える中、選ばれる理由を言語化する必要に迫られていた。',
      approach: [
        '院長・スタッフ・患者へのインタビューを通じて、組織の内側にある「意味」を掘り起こす',
        '30年の歴史の中に眠る「物語の核」を発見し、全員が語れる言葉に変換する',
        'スタッフ全員が自分の言葉で語れる「ブランドの軸」を設計し、研修で定着させる',
        '患者との接点（院内掲示・パンフレット・ウェブ）に一貫した言葉を実装する',
      ],
      process: [
        { phase: 'Phase 01', title: '意味の発掘', body: '院長・ベテランスタッフ・患者計23名へのインタビューを実施。「なぜここで働くのか」「なぜここに来るのか」という問いから、組織の核にある意味を掘り起こした。' },
        { phase: 'Phase 02', title: 'ブランド軸の設計', body: 'インタビューから浮かび上がった「生活の中にある医療」というコンセプトを言語化。ミッション・ビジョン・バリューを策定し、スタッフ全員で共有するワークショップを実施した。' },
        { phase: 'Phase 03', title: 'コンテンツ実装', body: '院内掲示・患者向けパンフレット・ウェブサイトのコンテンツを全面リニューアル。スタッフの声・患者の物語を中心に据えた構成に変更した。' },
        { phase: 'Phase 04', title: '循環の設計', body: 'スタッフが自ら発信できる仕組みを構築。月次の「物語の棚卸し」ミーティングを導入し、日々の実践が言葉として蓄積される体制を整えた。' },
      ],
      results: [
        { label: '紹介患者数', value: '前年比 +34%' },
        { label: 'スタッフ満足度', value: '前年比 +28pt' },
        { label: '院内掲示の更新頻度', value: '月1回 → 週1回' },
        { label: 'ウェブ経由の問い合わせ', value: '前年比 +52%' },
      ],
      quote: {
        text: '「うちの病院の良さ」を、スタッフが自分の言葉で語れるようになった。それが一番の変化です。',
        author: '院長',
      },
      images: [
        'https://readdy.ai/api/search-image?query=Japanese%20hospital%20reception%20area%2C%20warm%20interior%20design%2C%20natural%20wood%20elements%2C%20soft%20lighting%2C%20welcoming%20atmosphere%2C%20no%20people%2C%20architectural%20photography%2C%20beige%20and%20white%20tones&width=800&height=500&seq=case001a&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Japanese%20medical%20clinic%20waiting%20room%2C%20minimalist%20design%2C%20natural%20light%20through%20large%20windows%2C%20clean%20white%20and%20wood%20tones%2C%20no%20people%2C%20interior%20photography%2C%20calm%20atmosphere&width=800&height=500&seq=case001b&orientation=landscape',
      ],
      gallery: [
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20medical%20clinic%20website%20UI%20design%20displayed%20on%20iMac%20desktop%20screen%2C%20clean%20white%20minimalist%20interface%2C%20teal%20accent%20color%2C%20professional%20healthcare%20layout%2C%20soft%20studio%20light%2C%20no%20people%2C%20high%20quality%20product%20photography&width=1200&height=800&seq=g001a&orientation=landscape',
          label: 'Webサイト',
          caption: 'トップページ — 「生活の中にある医療」をビジュアルで体現したデザイン',
          aspect: 'landscape',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20hospital%20patient%20guide%20brochure%20design%2C%20elegant%20printed%20booklet%20open%20spread%2C%20cream%20ivory%20paper%2C%20refined%20serif%20typography%2C%20minimalist%20healthcare%20layout%2C%20flat%20lay%20on%20white%20surface%2C%20soft%20natural%20light%2C%20no%20people&width=800&height=800&seq=g001b&orientation=squarish',
          label: '患者向けパンフレット',
          caption: '患者向け案内冊子 — スタッフの声を中心に据えた構成',
          aspect: 'square',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20hospital%20brand%20identity%20design%2C%20logo%20variations%20on%20white%20background%2C%20clean%20minimal%20brand%20guidelines%2C%20business%20card%20stationery%20set%2C%20flat%20lay%2C%20soft%20natural%20light%2C%20warm%20neutral%20tones%2C%20no%20people%2C%20high-end%20product%20photography&width=800&height=800&seq=g001c&orientation=squarish',
          label: 'ロゴ・VI',
          caption: 'ブランドアイデンティティ — 「温かさ」と「信頼」を両立したVI設計',
          aspect: 'square',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20hospital%20interior%20wall%20signage%20design%2C%20clean%20minimalist%20wayfinding%20system%2C%20warm%20wood%20and%20white%20tones%2C%20modern%20healthcare%20environment%2C%20no%20people%2C%20architectural%20photography&width=1200&height=700&seq=g001d&orientation=landscape',
          label: '院内サイン',
          caption: '院内サイン・掲示物 — 統一されたビジュアル言語で空間を整備',
          aspect: 'landscape',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20medical%20clinic%20recruitment%20brochure%20design%2C%20elegant%20printed%20materials%2C%20cream%20and%20teal%20color%20palette%2C%20minimalist%20typography%2C%20staff%20introduction%20layout%2C%20flat%20lay%20on%20white%20marble%2C%20soft%20natural%20light%2C%20no%20people&width=800&height=1100&seq=g001e&orientation=portrait',
          label: '採用パンフレット',
          caption: '採用向け冊子 — 「なぜここで働くのか」を語る採用ブランディング',
          aspect: 'portrait',
        },
      ],
    },
  },
  {
    id: '02',
    category: 'Web制作',
    categoryEn: 'Website',
    tag: '福祉',
    tagEn: 'Welfare',
    tagColor: 'text-amber-700 border-amber-300',
    title: '障害福祉事業所の社会的認知向上と支援者獲得プロジェクト',
    subtitle: '現場の誠実さを、社会に届く言葉に翻訳する。',
    client: '障害福祉事業所',
    clientType: '就労継続支援B型・定員40名',
    before: '現場スタッフの献身的な取り組みが、外部にまったく伝わっていなかった。',
    after: '活動の意味が言語化され、寄付者が前年比89%増・ボランティア登録が156%増加。',
    period: '2022年4月〜2023年3月（12ヶ月）',
    scope: '意味設計 / コンテンツ制作 / 発信体制構築',
    deliverables: ['Webサイト全面リニューアル', '年次報告書デザイン', 'SNSコンテンツ設計', '支援者向けパンフレット', '発信体制の内製化支援'],
    img: 'https://readdy.ai/api/search-image?query=Japanese%20welfare%20facility%20interior%2C%20bright%20and%20warm%20community%20space%2C%20natural%20light%2C%20clean%20minimalist%20design%2C%20no%20people%2C%20architectural%20photography%2C%20warm%20wood%20tones&width=800&height=540&seq=case004&orientation=landscape',
    detail: {
      challenge: '障害のある方の就労支援・生活支援を行う事業所。現場スタッフは誠実に、丁寧に、利用者と向き合い続けていた。しかし、その取り組みが外部にまったく伝わっておらず、寄付・ボランティア・連携機関の獲得に苦労していた。',
      approach: [
        'スタッフ・利用者・家族へのインタビューで「この場所の意味」を言語化する',
        '福祉の専門用語を使わず、社会に届く言葉に翻訳する',
        '支援者が「関わりたい」と思える物語を設計する',
        '継続的に発信できる体制を内部に構築する',
      ],
      process: [
        { phase: 'Phase 01', title: '現場の意味を掘り起こす', body: 'スタッフ・利用者・家族・地域住民へのインタビューを実施。「この場所があることで、何が変わるのか」という問いから、活動の核にある意味を言語化した。' },
        { phase: 'Phase 02', title: '言葉の翻訳', body: '福祉の専門用語・制度の説明中心だった発信を、「人の変化の物語」中心に転換。支援者が「自分ごと」として受け取れる言葉に翻訳した。' },
        { phase: 'Phase 03', title: 'コンテンツの設計と制作', body: '年次報告書・ウェブサイト・SNSのコンテンツを全面リニューアル。利用者の日常・スタッフの思い・地域との関わりを物語として発信する構成に変更した。' },
        { phase: 'Phase 04', title: '発信体制の内製化', body: 'スタッフが自ら発信できるよう、コンテンツ制作の研修と仕組みを構築。外部依存から脱却し、日々の実践が自然と発信につながる体制を整えた。' },
      ],
      results: [
        { label: '寄付者数', value: '前年比 +89%' },
        { label: 'ボランティア登録数', value: '前年比 +156%' },
        { label: '連携機関数', value: '前年比 +34%' },
        { label: 'SNSフォロワー数', value: '6ヶ月で3.2倍' },
      ],
      quote: {
        text: '私たちがやっていることを、「すごいことをしている」と言ってもらえるようになった。現場の誇りが変わりました。',
        author: '施設長',
      },
      images: [
        'https://readdy.ai/api/search-image?query=Japanese%20welfare%20workshop%20space%2C%20bright%20interior%2C%20craft%20tables%2C%20natural%20light%2C%20warm%20atmosphere%2C%20no%20people%2C%20architectural%20photography%2C%20soft%20warm%20tones&width=800&height=500&seq=case004a&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Japanese%20community%20support%20center%2C%20welcoming%20entrance%2C%20natural%20wood%20interior%2C%20soft%20lighting%2C%20no%20people%2C%20architectural%20photography%2C%20warm%20beige%20tones&width=800&height=500&seq=case004b&orientation=landscape',
      ],
      gallery: [
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20welfare%20organization%20annual%20report%20design%2C%20elegant%20printed%20booklet%20open%20spread%2C%20warm%20amber%20and%20cream%20color%20palette%2C%20human-centered%20editorial%20layout%2C%20flat%20lay%20on%20light%20wood%20surface%2C%20soft%20natural%20light%2C%20no%20people%2C%20product%20photography&width=1200&height=800&seq=g002a&orientation=landscape',
          label: '年次報告書',
          caption: '年次報告書 — 制度説明から「人の変化の物語」へ全面転換',
          aspect: 'landscape',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20welfare%20facility%20website%20UI%20design%20on%20laptop%20screen%2C%20warm%20and%20welcoming%20interface%2C%20amber%20accent%20color%2C%20community-focused%20layout%2C%20soft%20studio%20light%2C%20no%20people%2C%20product%20photography&width=800&height=800&seq=g002b&orientation=squarish',
          label: 'Webサイト',
          caption: 'Webサイト — 利用者の日常とスタッフの思いを中心に据えた構成',
          aspect: 'square',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20welfare%20organization%20brand%20identity%20logo%20design%2C%20warm%20amber%20and%20cream%20color%20scheme%2C%20minimal%20clean%20logo%20on%20white%20background%2C%20stationery%20mockup%2C%20flat%20lay%2C%20soft%20natural%20light%2C%20no%20people&width=800&height=800&seq=g002c&orientation=squarish',
          label: 'ロゴ・VI',
          caption: 'ブランドアイデンティティ — 「温かさ」と「誠実さ」を体現したVI',
          aspect: 'square',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20welfare%20facility%20fundraising%20brochure%20design%2C%20elegant%20printed%20materials%2C%20warm%20amber%20cream%20color%20palette%2C%20donation%20appeal%20layout%2C%20flat%20lay%20on%20white%20surface%2C%20soft%20natural%20light%2C%20no%20people%2C%20editorial%20photography&width=800&height=1100&seq=g002d&orientation=portrait',
          label: '支援者向け冊子',
          caption: '支援者向けパンフレット — 「関わりたい」と思える物語設計',
          aspect: 'portrait',
        },
      ],
    },
  },
  {
    id: '03',
    category: 'グラフィック',
    categoryEn: 'Graphic',
    tag: '教育',
    tagEn: 'Education',
    tagColor: 'text-emerald-700 border-emerald-300',
    title: '私立学校の教育理念言語化と入学者増加に向けた価値設計',
    subtitle: '創立100年の歴史を、保護者に届く言葉に変える。',
    client: '私立中高一貫校',
    clientType: '生徒数800名・創立100年',
    before: '創立100年の歴史があるのに、「どんな学校か」を保護者に説明できていなかった。',
    after: '教育理念が言語化され、入学説明会の参加者が前年比170%・入学者数が23%増加。',
    period: '2022年9月〜2023年8月（12ヶ月）',
    scope: '意味設計 / コンテンツ戦略 / 入学案内リニューアル',
    deliverables: ['教育理念・スクールコンセプト策定', '入学案内冊子全面リニューアル', 'Webサイトコンテンツ設計', '入学説明会プログラム設計', 'スクールVI更新'],
    img: 'https://readdy.ai/api/search-image?query=Japanese%20school%20building%20exterior%2C%20traditional%20architecture%20with%20modern%20elements%2C%20cherry%20blossom%20trees%2C%20soft%20morning%20light%2C%20editorial%20photography%2C%20calm%20atmosphere%2C%20warm%20tones&width=800&height=540&seq=case002&orientation=landscape',
    detail: {
      challenge: '創立100年を超える伝統校。歴史と実績は十分にあるが、「どんな教育をしているのか」「卒業後にどんな人間になるのか」を保護者に伝えられていなかった。少子化の影響で入学者数が減少傾向にあり、学校の価値を言語化・可視化することが急務だった。',
      approach: [
        '教員・卒業生・在校生・保護者へのインタビューで「この学校でしか得られないもの」を発掘する',
        '100年の歴史の中に一貫して流れる「教育の哲学」を言語化する',
        '保護者が「ここに入れたい」と思える言葉と物語を設計する',
        '入学案内・説明会・ウェブを一貫したメッセージで統一する',
      ],
      process: [
        { phase: 'Phase 01', title: '歴史と現在の対話', body: '創立時の記録・教員インタビュー・卒業生インタビューを通じて、100年間変わらない「教育の核」を発掘。時代を超えて受け継がれてきた価値観を言語化した。' },
        { phase: 'Phase 02', title: '教育理念の再構築', body: '発掘した価値観をもとに、教育理念・スクールコンセプト・キャッチコピーを策定。教員全員が「自分たちの言葉」として誇りを持てる表現を追求した。' },
        { phase: 'Phase 03', title: '入学案内の全面リニューアル', body: '従来の「実績・カリキュラム紹介」中心の構成から、「この学校で育つとどんな人間になるか」を物語で伝える構成に変更。卒業生の声を中心に据えた。' },
        { phase: 'Phase 04', title: '説明会の体験設計', body: '入学説明会のプログラムを全面的に再設計。「学校を知る」から「学校の哲学を体験する」場に変え、参加者が自然と語り手になる仕掛けを組み込んだ。' },
      ],
      results: [
        { label: '入学説明会参加者数', value: '前年比 +170%' },
        { label: '入学者数', value: '前年比 +23%' },
        { label: 'ウェブ滞在時間', value: '前年比 +89%' },
        { label: '教員の理念共有度', value: '「十分に共有できている」前年比 +41pt' },
      ],
      quote: {
        text: '100年の歴史があっても、言葉にしなければ伝わらない。言語化して初めて、私たちの教育が「見えた」気がしました。',
        author: '校長',
      },
      images: [
        'https://readdy.ai/api/search-image?query=Japanese%20school%20classroom%20interior%2C%20traditional%20wooden%20desks%2C%20natural%20light%2C%20warm%20atmosphere%2C%20no%20students%2C%20editorial%20photography%2C%20soft%20warm%20tones&width=800&height=500&seq=case002a&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Japanese%20school%20library%20interior%2C%20wooden%20bookshelves%2C%20natural%20light%2C%20quiet%20reading%20space%2C%20no%20people%2C%20architectural%20photography%2C%20warm%20beige%20tones&width=800&height=500&seq=case002b&orientation=landscape',
      ],
      gallery: [
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20private%20school%20admission%20brochure%20design%2C%20elegant%20printed%20booklet%20open%20spread%2C%20cream%20ivory%20paper%2C%20refined%20serif%20typography%2C%20minimalist%20educational%20layout%2C%20flat%20lay%20on%20white%20marble%20surface%2C%20soft%20natural%20light%2C%20no%20people%2C%20editorial%20product%20photography&width=1200&height=800&seq=g003a&orientation=landscape',
          label: '入学案内',
          caption: '入学案内冊子 — 「この学校で育つとどんな人間になるか」を物語で伝える',
          aspect: 'landscape',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20private%20school%20website%20UI%20design%20on%20iMac%20screen%2C%20elegant%20academic%20interface%2C%20emerald%20green%20accent%20color%2C%20tradition%20and%20modernity%20balance%2C%20soft%20studio%20light%2C%20no%20people%2C%20product%20photography&width=800&height=800&seq=g003b&orientation=squarish',
          label: 'Webサイト',
          caption: 'Webサイト — 100年の歴史と現在の教育を一体で伝えるデザイン',
          aspect: 'square',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20school%20brand%20identity%20logo%20design%2C%20traditional%20yet%20modern%20aesthetic%2C%20emerald%20green%20and%20cream%20color%20scheme%2C%20school%20crest%20emblem%2C%20stationery%20set%20flat%20lay%2C%20soft%20natural%20light%2C%20no%20people%2C%20high-end%20product%20photography&width=800&height=800&seq=g003c&orientation=squarish',
          label: 'スクールVI',
          caption: 'スクールVI — 伝統と革新を両立したビジュアルアイデンティティ',
          aspect: 'square',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20school%20open%20campus%20event%20poster%20design%2C%20elegant%20minimalist%20layout%2C%20cream%20and%20dark%20green%20color%20palette%2C%20serif%20typography%2C%20printed%20poster%20on%20wall%2C%20editorial%20photography%2C%20no%20people&width=800&height=1100&seq=g003d&orientation=portrait',
          label: '説明会ポスター',
          caption: '入学説明会ポスター — 「体験する場」としての説明会を告知',
          aspect: 'portrait',
        },
      ],
    },
  },
  {
    id: '04',
    category: '映像制作',
    categoryEn: 'Video',
    tag: '地域',
    tagEn: 'Community',
    tagColor: 'text-sky-700 border-sky-300',
    title: '市民参加型まちづくりのコミュニケーション設計',
    subtitle: '行政と市民の「対話」を、構造として設計する。',
    client: '地方自治体',
    clientType: '人口8万人・総合計画策定プロジェクト',
    before: '市民参加を呼びかけても、参加者が固定化し、新たな声が集まらなかった。',
    after: '参加の意味と仕組みを再設計し、総参加者数が前回比234%・若年層参加が67%増加。',
    period: '2024年1月〜2024年12月（12ヶ月）',
    scope: '意味設計 / コミュニティ形成 / ワークショップ設計',
    deliverables: ['まちづくり広報誌の創刊', '市民参加Webプラットフォーム', 'ワークショップ設計・ファシリテーション', '参加者向け告知ポスター・チラシ', '総合計画書デザイン'],
    img: 'https://readdy.ai/api/search-image?query=Japanese%20community%20center%20meeting%20room%2C%20modern%20interior%2C%20round%20tables%2C%20natural%20light%2C%20clean%20minimalist%20space%2C%20no%20people%2C%20architectural%20photography%2C%20warm%20neutral%20tones&width=800&height=540&seq=case005&orientation=landscape',
    detail: {
      challenge: '人口減少が続く地方都市での総合計画策定にあたり、市民参加を促進したいが、参加者が固定化し「いつも同じ顔ぶれ」になっていた。若者・子育て世代・外国籍住民など、多様な声を集めることが課題だった。',
      approach: [
        '「参加する意味」を市民が実感できる場の設計から始める',
        '参加のハードルを下げる多様な接点を設計する',
        '参加者が「語り手」になり、次の参加者を呼ぶ循環を設計する',
        '行政内部の発信体制も同時に整備する',
      ],
      process: [
        { phase: 'Phase 01', title: '参加しない理由の発掘', body: '参加していない市民へのインタビューを実施。「参加しない理由」を丁寧に聞き取り、「参加する意味が見えない」「自分の声が届く気がしない」という本質的な課題を発掘した。' },
        { phase: 'Phase 02', title: '参加の意味の再設計', body: '「意見を言う場」から「まちの未来を一緒に考える場」へとコンセプトを転換。参加することで「自分のまちが変わる」という実感を設計した。' },
        { phase: 'Phase 03', title: '多様な参加接点の設計', body: 'オンライン・オフライン・非同期など、多様な参加方法を設計。子育て中の親向けの託児付きワークショップ、若者向けのSNS参加など、参加のハードルを下げる仕組みを構築した。' },
        { phase: 'Phase 04', title: '循環の仕組み化', body: '参加者が「語り手」になり、次の参加者を呼ぶ仕組みを設計。参加者の声を可視化し、「自分の意見が反映された」という実感を継続的に届ける体制を整えた。' },
      ],
      results: [
        { label: '総参加者数', value: '前回比 +234%' },
        { label: '20〜40代の参加比率', value: '前回比 +67%' },
        { label: '外国籍住民の参加', value: '初めて10名以上が参加' },
        { label: '市民満足度', value: '「意見が届いた実感がある」前回比 +44pt' },
      ],
      quote: {
        text: '「参加する意味」を設計することが、こんなにも大切だとは思っていませんでした。',
        author: '担当課長',
      },
      images: [
        'https://readdy.ai/api/search-image?query=Japanese%20city%20hall%20modern%20interior%2C%20open%20space%2C%20natural%20light%2C%20clean%20minimalist%20design%2C%20no%20people%2C%20architectural%20photography%2C%20warm%20neutral%20tones&width=800&height=500&seq=case005a&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Japanese%20public%20space%20community%20plaza%2C%20modern%20urban%20design%2C%20natural%20light%2C%20open%20architecture%2C%20no%20people%2C%20architectural%20photography%2C%20soft%20warm%20tones&width=800&height=500&seq=case005b&orientation=landscape',
      ],
      gallery: [
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20community%20newsletter%20magazine%20printed%20publication%2C%20multiple%20issues%20spread%20out%2C%20warm%20cream%20paper%20texture%2C%20clean%20editorial%20layout%20with%20Japanese%20typography%2C%20flat%20lay%20on%20light%20wood%20surface%2C%20soft%20natural%20light%2C%20no%20people%2C%20product%20photography&width=1200&height=800&seq=g004a&orientation=landscape',
          label: '広報誌',
          caption: 'まちづくり広報誌 — 市民の声を届ける参加型メディアとして創刊',
          aspect: 'landscape',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20city%20planning%20website%20UI%20design%20on%20laptop%20screen%2C%20clean%20civic%20interface%2C%20sky%20blue%20accent%20color%2C%20community%20participation%20platform%2C%20soft%20studio%20light%2C%20no%20people%2C%20product%20photography&width=800&height=800&seq=g004b&orientation=squarish',
          label: 'Webサイト',
          caption: '市民参加プラットフォーム — オンラインでの意見収集・可視化システム',
          aspect: 'square',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20community%20event%20workshop%20poster%20design%2C%20minimalist%20layout%2C%20sky%20blue%20and%20cream%20color%20palette%2C%20civic%20participation%20theme%2C%20printed%20poster%20flat%20lay%2C%20soft%20natural%20light%2C%20no%20people%2C%20editorial%20photography&width=800&height=1100&seq=g004c&orientation=portrait',
          label: 'ワークショップ告知',
          caption: 'ワークショップ告知ポスター — 多様な市民が参加したくなるビジュアル設計',
          aspect: 'portrait',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20city%20planning%20report%20design%2C%20elegant%20printed%20document%2C%20clean%20government%20publication%20layout%2C%20sky%20blue%20and%20white%20color%20scheme%2C%20flat%20lay%20on%20white%20surface%2C%20soft%20natural%20light%2C%20no%20people%2C%20product%20photography&width=800&height=800&seq=g004d&orientation=squarish',
          label: '計画書デザイン',
          caption: '総合計画書 — 市民が読みたくなる計画書のデザイン',
          aspect: 'square',
        },
      ],
    },
  },
  {
    id: '05',
    category: 'ブランディング',
    categoryEn: 'Branding',
    tag: '文化',
    tagEn: 'Culture',
    tagColor: 'text-violet-700 border-violet-300',
    title: '地方自治体の文化施設リブランディングと来場者創造',
    subtitle: '「誰のための施設か」を、地域全体で共有する。',
    client: '地方自治体文化施設',
    clientType: '築50年・リニューアルオープン',
    before: '文化施設のリニューアルを前に、「誰のための施設か」が内部でも共有されていなかった。',
    after: '地域文化の意味を再定義し、開館初年度から目標比143%の来場者と協賛を獲得。',
    period: '2023年1月〜2024年6月（18ヶ月）',
    scope: '意味設計 / ブランド戦略 / コミュニティ形成支援',
    deliverables: ['施設コンセプト策定', 'ロゴ・VI設計', 'Webサイト制作', '開館プログラム冊子', '施設内サイン計画', '地域メディア連携設計'],
    img: 'https://readdy.ai/api/search-image?query=Japanese%20cultural%20center%20interior%2C%20contemporary%20art%20gallery%20space%2C%20clean%20white%20walls%2C%20natural%20light%2C%20minimalist%20exhibition%20space%2C%20no%20people%2C%20architectural%20photography%2C%20soft%20neutral%20tones&width=800&height=540&seq=case003&orientation=landscape',
    detail: {
      challenge: '築50年の文化施設をリニューアルするにあたり、「誰のための施設か」「何のために存在するのか」が内部でも共有されていなかった。行政・文化団体・市民それぞれが異なるビジョンを持ち、方向性が定まらないまま開館日が迫っていた。',
      approach: [
        '行政・文化団体・市民・アーティストへのインタビューで「この地域の文化の意味」を掘り起こす',
        '多様なステークホルダーが共有できる「施設の存在意義」を言語化する',
        '来場者が「語り手」になる参加型の体験を設計する',
        '開館後の運営に引き継げる発信の仕組みを構築する',
      ],
      process: [
        { phase: 'Phase 01', title: '地域の文化の意味を掘り起こす', body: '行政担当者・文化団体・市民・アーティスト計41名へのインタビューと、地域の文化的資源の調査を実施。この地域にしかない「文化の核」を発掘した。' },
        { phase: 'Phase 02', title: '施設コンセプトの策定', body: '「この地域の文化を、次の世代に手渡す場所」というコンセプトを策定。行政・文化団体・市民が共に「自分たちの施設」として語れる言葉を設計した。' },
        { phase: 'Phase 03', title: '開館プログラムの設計', body: '開館記念プログラムを「地域の文化を体験する」から「地域の文化を語り合う」場として設計。市民が主役になるワークショップ・展示・イベントを企画した。' },
        { phase: 'Phase 04', title: '発信体制の構築', body: '施設スタッフが自ら発信できる体制を構築。地域メディア・SNS・コミュニティとの連携の仕組みを設計し、開館後も循環が続く構造を整えた。' },
      ],
      results: [
        { label: '開館初年度来場者数', value: '目標比 +143%' },
        { label: '協賛企業・団体数', value: '想定の2.3倍' },
        { label: 'リピーター率', value: '62%（初年度）' },
        { label: 'メディア掲載数', value: '全国メディア掲載12件（6ヶ月）' },
      ],
      quote: {
        text: '「誰のための施設か」という問いに、地域全体で答えを出せたことが、すべての出発点でした。',
        author: '施設長',
      },
      images: [
        'https://readdy.ai/api/search-image?query=Japanese%20cultural%20facility%20exterior%2C%20modern%20architecture%2C%20public%20plaza%2C%20natural%20light%2C%20clean%20minimalist%20design%2C%20no%20people%2C%20architectural%20photography%2C%20soft%20warm%20tones&width=800&height=500&seq=case003a&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Japanese%20art%20gallery%20exhibition%20space%2C%20contemporary%20interior%2C%20white%20walls%2C%20track%20lighting%2C%20minimalist%20design%2C%20no%20people%2C%20architectural%20photography%2C%20neutral%20tones&width=800&height=500&seq=case003b&orientation=landscape',
      ],
      gallery: [
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20cultural%20facility%20branding%20identity%20design%20system%2C%20elegant%20logo%20stationery%20mockup%20on%20cream%20paper%2C%20minimalist%20visual%20identity%2C%20business%20cards%20letterhead%20brochure%20flat%20lay%2C%20soft%20natural%20light%2C%20warm%20neutral%20tones%2C%20no%20people%2C%20high-end%20editorial%20product%20photography&width=1200&height=800&seq=g005a&orientation=landscape',
          label: 'ロゴ・VI',
          caption: 'ブランドアイデンティティ — 「地域の文化を次世代に手渡す」を体現したVI',
          aspect: 'landscape',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20cultural%20center%20website%20UI%20design%20on%20iMac%20screen%2C%20elegant%20arts%20institution%20interface%2C%20violet%20and%20cream%20color%20scheme%2C%20event%20program%20layout%2C%20soft%20studio%20light%2C%20no%20people%2C%20product%20photography&width=800&height=800&seq=g005b&orientation=squarish',
          label: 'Webサイト',
          caption: 'Webサイト — 施設の世界観と来場者への誘いを一体で設計',
          aspect: 'square',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20cultural%20event%20program%20booklet%20design%2C%20elegant%20printed%20publication%2C%20cream%20and%20dark%20violet%20color%20palette%2C%20arts%20festival%20program%20layout%2C%20flat%20lay%20on%20white%20surface%2C%20soft%20natural%20light%2C%20no%20people%2C%20editorial%20photography&width=800&height=1100&seq=g005c&orientation=portrait',
          label: '開館プログラム冊子',
          caption: '開館記念プログラム冊子 — 市民が主役になる体験を告知',
          aspect: 'portrait',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20cultural%20facility%20exhibition%20poster%20design%2C%20minimalist%20arts%20poster%2C%20cream%20and%20dark%20charcoal%20color%20palette%2C%20elegant%20typography%2C%20printed%20poster%20on%20gallery%20wall%2C%20editorial%20photography%2C%20no%20people&width=800&height=800&seq=g005d&orientation=squarish',
          label: '展示ポスター',
          caption: '展示告知ポスター — 地域アーティストとの協働展示を告知',
          aspect: 'square',
        },
      ],
    },
  },
  {
    id: '06',
    category: 'Web制作',
    categoryEn: 'Website',
    tag: '教育',
    tagEn: 'Education',
    tagColor: 'text-emerald-700 border-emerald-300',
    title: '大学附属研究機関の社会発信力強化プロジェクト',
    subtitle: '研究の価値を、社会に届く物語に変える。',
    client: '大学附属研究機関',
    clientType: '医療・福祉分野・研究員数45名',
    before: '優れた研究成果があるのに、社会への発信が論文・プレスリリースにとどまっていた。',
    after: '研究の意味を物語化し、メディア掲載が前年比312%・産学連携が78%増加。',
    period: '2023年6月〜2024年5月（12ヶ月）',
    scope: 'コンテンツ戦略 / ストーリーテリング設計 / 発信体制構築',
    deliverables: ['Webサイト全面リニューアル', '年次報告書デザイン', '産学連携向け資料', '研究ニュースレター', 'SNS発信体制構築'],
    img: 'https://readdy.ai/api/search-image?query=Japanese%20university%20research%20laboratory%20interior%2C%20modern%20academic%20space%2C%20clean%20white%20walls%2C%20natural%20light%2C%20minimalist%20design%2C%20no%20people%2C%20architectural%20photography%2C%20soft%20neutral%20tones&width=800&height=540&seq=case006&orientation=landscape',
    detail: {
      challenge: '医療・福祉分野の先端研究を行う大学附属研究機関。研究の質は高く、国際的な評価も得ていたが、社会への発信が論文・プレスリリースにとどまり、一般市民・企業・寄付者には届いていなかった。',
      approach: [
        '研究者へのインタビューで「研究の社会的意味」を言語化する',
        '専門用語を使わず、社会に届く物語に翻訳する',
        '研究者自身が発信者になれる体制を構築する',
        'メディア・企業・寄付者それぞれへの発信戦略を設計する',
      ],
      process: [
        { phase: 'Phase 01', title: '研究の意味の言語化', body: '研究者へのインタビューを通じて、「この研究が実現したら、社会はどう変わるのか」を言語化。専門的な研究内容を「社会への影響」という視点で再解釈した。' },
        { phase: 'Phase 02', title: 'ストーリーテリングの設計', body: '研究の背景・プロセス・未来への展望を「物語」として再構成。「なぜこの研究をするのか」という研究者の思いを中心に据えたコンテンツ設計を行った。' },
        { phase: 'Phase 03', title: '発信チャネルの整備', body: 'ウェブサイト・SNS・メディアリレーションズを整備。研究者が自ら発信できるよう、コンテンツ制作の研修と簡易なワークフローを構築した。' },
        { phase: 'Phase 04', title: '産学連携・寄付への展開', body: '企業・財団向けの発信資料を設計。「この研究に関わる意味」を明確に伝えることで、産学連携・寄付の獲得につなげた。' },
      ],
      results: [
        { label: 'メディア掲載数', value: '前年比 +312%' },
        { label: '産学連携件数', value: '前年比 +78%' },
        { label: '寄付・助成金獲得額', value: '前年比 +145%' },
        { label: 'ウェブ訪問者数', value: '前年比 +423%' },
      ],
      quote: {
        text: '研究の価値を「社会の言葉」で語れるようになって、初めて社会とつながれた気がしました。',
        author: '研究機関長',
      },
      images: [
        'https://readdy.ai/api/search-image?query=Japanese%20university%20campus%20building%20exterior%2C%20modern%20academic%20architecture%2C%20natural%20light%2C%20clean%20minimalist%20design%2C%20no%20people%2C%20architectural%20photography%2C%20soft%20warm%20tones&width=800&height=500&seq=case006a&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Japanese%20university%20seminar%20room%2C%20modern%20interior%2C%20natural%20light%2C%20clean%20white%20walls%2C%20academic%20atmosphere%2C%20no%20people%2C%20architectural%20photography%2C%20neutral%20tones&width=800&height=500&seq=case006b&orientation=landscape',
      ],
      gallery: [
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20university%20research%20institute%20website%20design%20on%20MacBook%20laptop%2C%20academic%20clean%20minimal%20UI%2C%20white%20and%20warm%20neutral%20color%20scheme%2C%20professional%20information%20architecture%2C%20soft%20studio%20light%2C%20no%20people%2C%20product%20photography&width=1200&height=800&seq=g006a&orientation=landscape',
          label: 'Webサイト',
          caption: 'Webサイト — 研究の意味を社会に届けるストーリーテリング設計',
          aspect: 'landscape',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20research%20institution%20annual%20report%20design%2C%20elegant%20printed%20booklet%20open%20spread%2C%20cream%20and%20dark%20charcoal%20color%20palette%2C%20academic%20editorial%20layout%2C%20flat%20lay%20on%20white%20surface%2C%20soft%20natural%20light%2C%20no%20people%2C%20product%20photography&width=800&height=800&seq=g006b&orientation=squarish',
          label: '年次報告書',
          caption: '年次報告書 — 研究成果を「社会への影響」として語る構成',
          aspect: 'square',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20academic%20research%20brochure%20design%20for%20corporate%20partnership%2C%20elegant%20printed%20materials%2C%20cream%20and%20emerald%20green%20color%20palette%2C%20industry%20collaboration%20appeal%20layout%2C%20flat%20lay%20on%20white%20marble%2C%20soft%20natural%20light%2C%20no%20people&width=800&height=1100&seq=g006c&orientation=portrait',
          label: '産学連携資料',
          caption: '産学連携向け資料 — 「この研究に関わる意味」を企業に伝える',
          aspect: 'portrait',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20research%20institute%20newsletter%20magazine%20design%2C%20printed%20publication%2C%20academic%20editorial%20layout%2C%20cream%20paper%20texture%2C%20clean%20typography%2C%20flat%20lay%20on%20light%20wood%2C%20soft%20natural%20light%2C%20no%20people%2C%20product%20photography&width=800&height=800&seq=g006d&orientation=squarish',
          label: '研究ニュースレター',
          caption: '研究ニュースレター — 一般市民に届く言葉で研究を発信',
          aspect: 'square',
        },
      ],
    },
  },
  {
    id: '07',
    category: 'グラフィック',
    categoryEn: 'Graphic',
    tag: '医療',
    tagEn: 'Healthcare',
    tagColor: 'text-rose-700 border-rose-300',
    title: '訪問看護ステーションの採用ブランディングと組織文化の可視化',
    subtitle: '「なぜここで働くのか」を、言葉と場で体現する。',
    client: '訪問看護ステーション',
    clientType: '看護師・理学療法士等 スタッフ数68名',
    before: '採用に苦労し、入職後の早期離職も続いていた。「うちの良さ」を言葉にできていなかった。',
    after: '組織文化を言語化・可視化し、採用応募数が前年比210%・1年以内離職率が半減。',
    period: '2024年3月〜2024年12月（10ヶ月）',
    scope: '採用ブランディング / 組織文化設計 / 採用コンテンツ制作',
    deliverables: ['採用コンセプト策定', '採用パンフレット制作', '採用Webページ設計', 'スタッフインタビュー動画', '入職者向けオンボーディング冊子'],
    img: 'https://readdy.ai/api/search-image?query=Japanese%20home%20care%20nursing%20office%20interior%2C%20warm%20and%20welcoming%20workspace%2C%20natural%20light%2C%20clean%20minimalist%20design%2C%20no%20people%2C%20architectural%20photography%2C%20soft%20warm%20beige%20tones&width=800&height=540&seq=case007&orientation=landscape',
    detail: {
      challenge: '地域に根ざした訪問看護ステーション。スタッフは誠実に利用者と向き合っているが、「なぜここで働くのか」を言葉にできず、採用に苦労していた。入職後も「思っていたのと違う」という早期離職が続いていた。',
      approach: [
        'スタッフへのインタビューで「この職場の意味」を言語化する',
        '採用候補者が「ここで働きたい」と思える物語を設計する',
        '入職後のギャップをなくす「正直な採用コンテンツ」を制作する',
        '組織文化を日常的に可視化する仕組みを構築する',
      ],
      process: [
        { phase: 'Phase 01', title: '組織文化の言語化', body: 'スタッフ全員へのインタビューを実施。「なぜここで働き続けるのか」「この仕事の何が好きか」という問いから、組織の核にある文化を言語化した。' },
        { phase: 'Phase 02', title: '採用コンセプトの策定', body: '言語化した組織文化をもとに、採用コンセプトを策定。「正直に、丁寧に、伝える」という方針のもと、良い面も課題も含めた誠実な採用コンテンツを設計した。' },
        { phase: 'Phase 03', title: '採用コンテンツの制作', body: '採用パンフレット・Webページ・スタッフインタビュー動画を制作。スタッフの「生の声」を中心に据え、入職後のリアルな日常を伝えるコンテンツを制作した。' },
        { phase: 'Phase 04', title: 'オンボーディングの設計', body: '入職者向けのオンボーディング冊子を制作。組織の文化・価値観・日常の実践を丁寧に伝え、入職後のギャップを最小化する仕組みを整えた。' },
      ],
      results: [
        { label: '採用応募数', value: '前年比 +210%' },
        { label: '1年以内離職率', value: '前年比 -52%' },
        { label: 'スタッフ満足度', value: '「この職場で働いて良かった」前年比 +38pt' },
        { label: '採用コスト', value: '前年比 -34%' },
      ],
      quote: {
        text: '「うちの良さ」を言葉にしてもらって、初めて自分たちの仕事に誇りを持てた気がします。',
        author: '管理者',
      },
      images: [
        'https://readdy.ai/api/search-image?query=Japanese%20home%20care%20nursing%20team%20meeting%20room%2C%20warm%20interior%2C%20natural%20light%2C%20clean%20minimalist%20design%2C%20no%20people%2C%20architectural%20photography%2C%20soft%20warm%20tones&width=800&height=500&seq=case007a&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Japanese%20nursing%20office%20reception%20area%2C%20welcoming%20entrance%2C%20natural%20wood%20interior%2C%20soft%20lighting%2C%20no%20people%2C%20architectural%20photography%2C%20warm%20beige%20tones&width=800&height=500&seq=case007b&orientation=landscape',
      ],
      gallery: [
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20home%20care%20nursing%20recruitment%20brochure%20design%2C%20elegant%20printed%20booklet%20open%20spread%2C%20warm%20cream%20and%20rose%20color%20palette%2C%20staff%20interview%20editorial%20layout%2C%20flat%20lay%20on%20light%20wood%20surface%2C%20soft%20natural%20light%2C%20no%20people%2C%20product%20photography&width=1200&height=800&seq=g007a&orientation=landscape',
          label: '採用パンフレット',
          caption: '採用パンフレット — スタッフの「生の声」を中心に据えた誠実な採用コンテンツ',
          aspect: 'landscape',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20nursing%20station%20recruitment%20website%20UI%20design%20on%20laptop%20screen%2C%20warm%20and%20welcoming%20interface%2C%20rose%20accent%20color%2C%20staff-centered%20layout%2C%20soft%20studio%20light%2C%20no%20people%2C%20product%20photography&width=800&height=800&seq=g007b&orientation=squarish',
          label: '採用Webページ',
          caption: '採用Webページ — 入職後のリアルな日常を伝えるコンテンツ設計',
          aspect: 'square',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20nursing%20onboarding%20handbook%20design%2C%20elegant%20printed%20booklet%2C%20warm%20cream%20and%20soft%20rose%20color%20palette%2C%20welcoming%20organizational%20culture%20layout%2C%20flat%20lay%20on%20white%20surface%2C%20soft%20natural%20light%2C%20no%20people&width=800&height=1100&seq=g007c&orientation=portrait',
          label: 'オンボーディング冊子',
          caption: 'オンボーディング冊子 — 入職後のギャップをなくす「正直な」組織案内',
          aspect: 'portrait',
        },
      ],
    },
  },
  {
    id: '08',
    category: '映像制作',
    categoryEn: 'Video',
    tag: '文化',
    tagEn: 'Culture',
    tagColor: 'text-violet-700 border-violet-300',
    title: '地域アーカイブプロジェクトの記録映像と発信設計',
    subtitle: '消えゆく記憶を、次の世代に手渡す形に変える。',
    client: 'NPO法人・地域文化保存団体',
    clientType: '会員数120名・設立15年',
    before: '地域の貴重な記録が散逸しており、次世代への継承方法が見つかっていなかった。',
    after: '記録映像と発信設計により、地域内外からの関心が高まり、継承活動の参加者が3倍に。',
    period: '2023年10月〜2024年9月（12ヶ月）',
    scope: '映像制作 / アーカイブ設計 / 発信体制構築',
    deliverables: ['記録映像シリーズ（全8本）', 'アーカイブWebサイト', '展示用映像コンテンツ', '広報用ショートムービー', '継承活動向け冊子'],
    img: 'https://readdy.ai/api/search-image?query=Japanese%20traditional%20craft%20workshop%20interior%2C%20warm%20natural%20light%2C%20wooden%20tools%20and%20materials%2C%20artisan%20workspace%2C%20no%20people%2C%20documentary%20photography%2C%20soft%20warm%20tones%2C%20film%20grain%20texture&width=800&height=540&seq=case008&orientation=landscape',
    detail: {
      challenge: '地域の伝統文化・民俗・産業の記録を保存してきたNPO。しかし、記録が散逸しており、次世代への継承方法が見つかっていなかった。高齢化が進む中、記録を持つ人々が減り続けていた。',
      approach: [
        '記録を「保存する」から「伝える」へと目的を転換する',
        '次世代が「関わりたい」と思える形で記録を再構成する',
        '地域内外に届く発信設計を行う',
        '継承活動が自走できる体制を構築する',
      ],
      process: [
        { phase: 'Phase 01', title: '記録の棚卸しと優先順位付け', body: '散逸した記録を整理し、緊急性・希少性・伝達可能性の観点から優先順位を付けた。記録を持つ方々へのインタビューを通じて、記録の「意味」を言語化した。' },
        { phase: 'Phase 02', title: '映像制作の設計', body: '「保存のための記録」から「伝えるための物語」へと映像の目的を転換。次世代が「自分ごと」として受け取れる構成を設計した。' },
        { phase: 'Phase 03', title: '映像制作と編集', body: '記録映像シリーズ全8本を制作。技術の記録だけでなく、「なぜこれを続けてきたのか」という人の物語を中心に据えた構成で制作した。' },
        { phase: 'Phase 04', title: 'アーカイブと発信', body: 'アーカイブWebサイトを構築し、映像・写真・テキストを統合した形で公開。地域内外への発信設計を行い、継承活動への参加を促す仕組みを整えた。' },
      ],
      results: [
        { label: '継承活動参加者数', value: '前年比 +200%（3倍）' },
        { label: 'アーカイブサイト訪問者', value: '公開3ヶ月で1.2万人' },
        { label: 'メディア掲載', value: '地域・全国メディア計8件' },
        { label: '新規会員数', value: '前年比 +145%' },
      ],
      quote: {
        text: '「記録を残す」ことと「伝える」ことは、全然違うことだと気づきました。',
        author: '代表理事',
      },
      images: [
        'https://readdy.ai/api/search-image?query=Japanese%20traditional%20craft%20demonstration%2C%20artisan%20hands%20working%20with%20natural%20materials%2C%20warm%20documentary%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20faces%20visible%2C%20cinematic%20photography&width=800&height=500&seq=case008a&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Japanese%20cultural%20archive%20exhibition%20space%2C%20documentary%20photographs%20on%20white%20walls%2C%20warm%20gallery%20lighting%2C%20minimalist%20display%2C%20no%20people%2C%20architectural%20photography&width=800&height=500&seq=case008b&orientation=landscape',
      ],
      gallery: [
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20documentary%20film%20still%20frame%2C%20traditional%20craft%20artisan%20workshop%2C%20warm%20cinematic%20lighting%2C%20film%20grain%20texture%2C%20shallow%20depth%20of%20field%2C%20no%20faces%2C%20high%20quality%20cinematography&width=1200&height=800&seq=g008a&orientation=landscape',
          label: '記録映像',
          caption: '記録映像シリーズ — 「なぜ続けてきたのか」を中心に据えた物語映像',
          aspect: 'landscape',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20cultural%20archive%20website%20UI%20design%20on%20laptop%20screen%2C%20clean%20minimal%20interface%2C%20warm%20cream%20and%20charcoal%20color%20scheme%2C%20documentary%20archive%20layout%2C%20soft%20studio%20light%2C%20no%20people%2C%20product%20photography&width=800&height=800&seq=g008b&orientation=squarish',
          label: 'アーカイブサイト',
          caption: 'アーカイブWebサイト — 映像・写真・テキストを統合した記録の場',
          aspect: 'square',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=Japanese%20cultural%20heritage%20exhibition%20booklet%20design%2C%20elegant%20printed%20publication%2C%20warm%20cream%20and%20dark%20charcoal%20color%20palette%2C%20documentary%20editorial%20layout%2C%20flat%20lay%20on%20light%20wood%2C%20soft%20natural%20light%2C%20no%20people&width=800&height=1100&seq=g008c&orientation=portrait',
          label: '継承活動冊子',
          caption: '継承活動向け冊子 — 次世代が「関わりたい」と思える参加案内',
          aspect: 'portrait',
        },
      ],
    },
  },
];
